import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumb from "../../common/breadcrumb";
import CommonLoader from "../../common/commonLoader";
import {
  getVendorRegistrationById,
  getVendorAmendmentByVendorRegistrationId,
  saveOrUpdateVendorAmendment,
  checkVendorRegistrationStatutoryDuplicate,
  getAllPaymentTerms,
} from "../../api/ap_services";
import {
  Step1CompanyDetails,
  Step2BusinessDetails,
  Step3FactoryInfo,
  Step4Statutory,
} from "../vendorRegistrationForm/steps";
import FormikTextField from "../vendorRegistrationForm/steps/FormikTextField";
import {
  mapApiResponseToFormValues,
  initialValues,
  validationSchema,
  VENDOR_PROFILE_SUBMIT_FIELDS,
  buildVendorRegistrationPayload,
  getVendorProfileSnapshot,
} from "./vendorProfileForm";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  submitRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const PROFILE_SECTIONS = [
  { title: "Company Details", key: "company" },
  { title: "Factory Information", key: "factory" },
  { title: "Business Details", key: "business" },
  { title: "Statutory Details", key: "statutory" },
];

const isApVendorUser = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
    return Number(userData.groupId) === 0 && Number(userData.locationId) === 2;
  } catch {
    return false;
  }
};

const VendorProfile = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [vendorRegistrationId, setVendorRegistrationId] = useState(null);
  const [vendorAmendmentId, setVendorAmendmentId] = useState(null);
  const [registrationBaseline, setRegistrationBaseline] = useState(null);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const baselineSnapshotRef = useRef(null);
  const [statutoryDuplicateDialog, setStatutoryDuplicateDialog] = useState({
    open: false,
    message: "",
  });
  const [ownerContactDuplicateDialog, setOwnerContactDuplicateDialog] =
    useState({ open: false });

  const vendorRegistrationListId = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}")?.userId;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!isApVendorUser()) {
      navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    getAllPaymentTerms(0, 9999)
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setPaymentTermsOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setPaymentTermsOptions([]));
  }, []);

  useEffect(() => {
    if (!isApVendorUser()) return;
    if (vendorRegistrationListId == null || vendorRegistrationListId === "") {
      setInitialLoading(false);
      setSelectedForEdit(null);
      return;
    }
    setInitialLoading(true);
    getVendorRegistrationById(vendorRegistrationListId)
      .then(async (res) => {
        const raw = res?.response?.responseObject ?? res?.response;
        const data = Array.isArray(raw) ? raw[0] : raw;
        const hasValidRecord =
          data &&
          typeof data === "object" &&
          (data.vendorRegistrationId != null || data.id != null);
        if (!hasValidRecord) {
          setSelectedForEdit(null);
          setRegistrationBaseline(null);
          setVendorAmendmentId(null);
          baselineSnapshotRef.current = null;
          return;
        }

        const id = data.vendorRegistrationId ?? data.id;
        setVendorRegistrationId(id);
        setRegistrationBaseline(data);

        let formSource = data;
        setVendorAmendmentId(null);
        if (id != null) {
          try {
            const amendRes = await getVendorAmendmentByVendorRegistrationId(id);
            const amend =
              amendRes?.response?.responseObject ?? amendRes?.response;
            const status = String(
              amend?.amendmentStatus ?? amend?.amendment_status ?? "",
            ).toUpperCase();
            if (amend && status === "PENDING") {
              const pendingId =
                amend.vendorAmendmentId ?? amend.vendor_amendment_id;
              if (pendingId != null) {
                setVendorAmendmentId(pendingId);
                formSource = amend;
              }
            }
          } catch {
            /* no pending amendment */
          }
        }

        setSelectedForEdit(formSource);
        const regFormVals = mapApiResponseToFormValues(data, initialValues);
        baselineSnapshotRef.current = getVendorProfileSnapshot(regFormVals);
      })
      .catch(() => {
        setSelectedForEdit(null);
        setRegistrationBaseline(null);
        setVendorAmendmentId(null);
        baselineSnapshotRef.current = null;
      })
      .finally(() => setInitialLoading(false));
  }, [vendorRegistrationListId]);

  const openStatutoryDuplicateDialog = useCallback((message) => {
    setStatutoryDuplicateDialog({
      open: true,
      message: String(message || ""),
    });
  }, []);

  const closeStatutoryDuplicateDialog = useCallback(() => {
    setStatutoryDuplicateDialog({ open: false, message: "" });
  }, []);

  const openOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: true });
  }, []);

  const closeOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: false });
  }, []);

  if (!isApVendorUser()) {
    return null;
  }

  if (initialLoading) {
    return (
      <Fragment>
        <CommonLoader />
        <Breadcrumb title="Vendor Profile" />
      </Fragment>
    );
  }

  const noDataFound = selectedForEdit === null;

  if (noDataFound) {
    return (
      <Fragment>
        <Breadcrumb title="Vendor Profile" />
        <Container fluid>
          <Card className={classes.card}>
            <CardContent>
              <Typography
                variant="h6"
                color="textSecondary"
                align="center"
                sx={{ py: 4 }}
              >
                No vendor registration details found for your account.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Vendor Profile" />
      <Container fluid>
        <Card className={classes.card}>
          <CardContent>
            <Formik
              key={selectedForEdit?.vendorRegistrationId ?? "vendor-profile"}
              initialValues={
                selectedForEdit
                  ? mapApiResponseToFormValues(selectedForEdit, initialValues)
                  : initialValues
              }
              validationSchema={validationSchema.pick(
                VENDOR_PROFILE_SUBMIT_FIELDS,
              )}
              enableReinitialize
              validateOnChange
              validateOnBlur
              onSubmit={async (values, { setSubmitting }) => {
                if (values.isOwnerContactNoAlreadyExists === "Y") {
                  toast.error(
                    "This owner contact number is already registered for another vendor.",
                  );
                  setSubmitting(false);
                  return;
                }
                const isPendingAmendmentUpdate = vendorAmendmentId != null;
                try {
                  const payload = buildVendorRegistrationPayload(
                    values,
                    VENDOR_PROFILE_SUBMIT_FIELDS.filter(
                      (f) => f !== "overallComments",
                    ),
                  );
                  payload.overallComments = values.overallComments;
                  payload.vendorRegistrationId = vendorRegistrationId;
                  if (vendorAmendmentId) {
                    payload.vendorAmendmentId = vendorAmendmentId;
                  }
                  payload.amendmentStatus = "PENDING";
                  payload.regNo = values.regNo;
                  payload.preparedBy = values.preparedBy;

                  const res = await saveOrUpdateVendorAmendment(payload);
                  const body = res?.response ?? {};
                  const isSuccess = body?.responseCode === true;

                  if (!isSuccess) {
                    toast.error(
                      body?.message ||
                        "Failed to submit profile amendment. Please try again.",
                    );
                    return;
                  }

                  const saved = body?.responseObject ?? body;
                  const savedId =
                    saved?.vendorAmendmentId ??
                    saved?.vendor_amendment_id ??
                    saved?.id;
                  if (savedId != null) {
                    setVendorAmendmentId(savedId);
                  }
                  baselineSnapshotRef.current = getVendorProfileSnapshot(values);
                  toast.success(
                    body?.message ||
                      (isPendingAmendmentUpdate
                        ? "Pending profile amendment updated successfully."
                        : "Profile amendment submitted successfully. Changes are pending approval."),
                  );
                } catch (err) {
                  toast.error(
                    err?.response?.data?.message ||
                      err?.message ||
                      "Failed to submit profile amendment. Please try again.",
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                setTouched,
                handleBlur,
                isSubmitting,
              }) => {
                const isVendorUser = true;
                const isDirty =
                  baselineSnapshotRef.current != null &&
                  getVendorProfileSnapshot(values) !==
                    baselineSnapshotRef.current;

                const registrationSource = registrationBaseline ?? selectedForEdit;

                const originalOwnerContact =
                  registrationSource?.ownerContactNo ??
                  registrationSource?.owner_contact_no ??
                  "";

                const originalStatutory = {
                  pan:
                    registrationSource?.statutoryPanNo ??
                    registrationSource?.statutorypan_no ??
                    "",
                  gst:
                    registrationSource?.statutoryGstNo ??
                    registrationSource?.statutorygst_no ??
                    "",
                  account:
                    registrationSource?.statutoryAccountNumber ??
                    registrationSource?.statutoryaccount_number ??
                    "",
                };
                const originalDuplicateFlags = {
                  isPanNoAlreadyExists:
                    registrationSource?.isPanNoAlreadyExists ??
                    registrationSource?.is_pan_no_already_exists ??
                    "N",
                  isGstNoAlreadyExists:
                    registrationSource?.isGstNoAlreadyExists ??
                    registrationSource?.is_gst_no_already_exists ??
                    "N",
                  isAccNoAlreadyExists:
                    registrationSource?.isAccNoAlreadyExists ??
                    registrationSource?.is_acc_no_already_exists ??
                    "N",
                };

                const handleOwnerContactBlur = (e) => {
                  handleBlur(e);
                  const raw = values.ownerContactNo;
                  if (raw == null || String(raw).trim() === "") {
                    setFieldValue("isOwnerContactNoAlreadyExists", "N");
                    return;
                  }
                  const trimmed = String(raw).trim();
                  if (!/^\d{10}$/.test(trimmed)) return;
                  const originalTrimmed =
                    originalOwnerContact != null
                      ? String(originalOwnerContact).trim()
                      : "";
                  if (
                    vendorRegistrationId != null &&
                    vendorRegistrationId !== "" &&
                    originalTrimmed !== "" &&
                    trimmed === originalTrimmed
                  ) {
                    return;
                  }
                  checkVendorRegistrationStatutoryDuplicate(
                    "OWNER_CONTACT",
                    trimmed,
                    vendorRegistrationId,
                  )
                    .then((res) => {
                      if (res.exists) {
                        setFieldValue("isOwnerContactNoAlreadyExists", "Y");
                        openOwnerContactDuplicateDialog();
                      } else {
                        setFieldValue("isOwnerContactNoAlreadyExists", "N");
                      }
                    })
                    .catch(() =>
                      setFieldValue("isOwnerContactNoAlreadyExists", "N"),
                    );
                };

                const handleStatutoryDuplicateBlur = (e) => {
                  handleBlur(e);
                  const name = e.target.name;
                  if (
                    name !== "statutoryPanNo" &&
                    name !== "statutoryGstNo" &&
                    name !== "statutoryAccountNumber"
                  ) {
                    return;
                  }
                  if (name === "statutoryGstNo" && values.gstApplicable !== "Y") {
                    return;
                  }
                  const FIELD_META = {
                    statutoryPanNo: {
                      type: "PAN",
                      flag: "isPanNoAlreadyExists",
                      message:
                        "This PAN number is already registered for another vendor.",
                    },
                    statutoryGstNo: {
                      type: "GST",
                      flag: "isGstNoAlreadyExists",
                      message:
                        "This GST number is already registered for another vendor.",
                    },
                    statutoryAccountNumber: {
                      type: "ACCOUNT",
                      flag: "isAccNoAlreadyExists",
                      message:
                        "This account number is already registered for another vendor.",
                    },
                  };
                  const meta = FIELD_META[name];
                  const raw = values[name];
                  if (raw == null || String(raw).trim() === "") {
                    setFieldValue(meta.flag, "N");
                    return;
                  }
                  const trimmed = String(raw).trim();
                  const originalRaw =
                    name === "statutoryPanNo"
                      ? originalStatutory.pan
                      : name === "statutoryGstNo"
                        ? originalStatutory.gst
                        : originalStatutory.account;
                  const originalTrimmed =
                    originalRaw != null ? String(originalRaw).trim() : "";
                  if (
                    vendorRegistrationId != null &&
                    vendorRegistrationId !== "" &&
                    originalTrimmed !== "" &&
                    trimmed === originalTrimmed
                  ) {
                    const prevFlag =
                      String(originalDuplicateFlags[meta.flag] ?? "N")
                        .toUpperCase() === "Y"
                        ? "Y"
                        : "N";
                    setFieldValue(meta.flag, prevFlag);
                    return;
                  }
                  checkVendorRegistrationStatutoryDuplicate(
                    meta.type,
                    trimmed,
                    vendorRegistrationId,
                  )
                    .then((res) => {
                      if (res.exists) {
                        setFieldValue(meta.flag, "Y");
                        openStatutoryDuplicateDialog(meta.message);
                      } else {
                        setFieldValue(meta.flag, "N");
                      }
                    })
                    .catch(() => setFieldValue(meta.flag, "N"));
                };

                const renderSection = (sectionKey) => {
                  switch (sectionKey) {
                    case "company":
                      return (
                        <Step1CompanyDetails
                          formData={values}
                          onFieldChange={handleChange}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          onOwnerContactBlur={handleOwnerContactBlur}
                          groupId={{ isAdmin: false }}
                          isEdit
                          isVendorUser={isVendorUser}
                        />
                      );
                    case "factory":
                      return (
                        <Step3FactoryInfo
                          formData={values}
                          onFieldChange={handleChange}
                          setFieldValue={setFieldValue}
                          setFormData={(updater) => {
                            const next =
                              typeof updater === "function"
                                ? updater(values)
                                : updater;
                            Object.keys(next).forEach((key) =>
                              setFieldValue(key, next[key]),
                            );
                          }}
                          errors={errors}
                          touched={touched}
                        />
                      );
                    case "business":
                      return (
                        <Step2BusinessDetails
                          formData={values}
                          onFieldChange={handleChange}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      );
                    case "statutory":
                      return (
                        <Step4Statutory
                          formData={values}
                          setFieldValue={setFieldValue}
                          onStatutoryDuplicateBlur={
                            handleStatutoryDuplicateBlur
                          }
                          errors={errors}
                          touched={touched}
                          paymentTermsOptions={paymentTermsOptions}
                          isVendorUser={isVendorUser}
                        />
                      );
                    default:
                      return null;
                  }
                };

                return (
                  <Form>
                    {PROFILE_SECTIONS.map((section, index) => (
                      <Box key={section.key} className={classes.section}>
                        {index > 0 && <Divider sx={{ mb: 3 }} />}
                        <Typography
                          variant="h6"
                          className={classes.sectionTitle}
                        >
                          {section.title}
                        </Typography>
                        {renderSection(section.key)}
                      </Box>
                    ))}

                    <Box className={classes.section}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography variant="h6" className={classes.sectionTitle}>
                        Overall Comments
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormikTextField
                            multiline
                            rows={4}
                            label="Overall Comments and General Observations"
                            name="overallComments"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box className={classes.submitRow}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !isDirty}
                        onClick={() => {
                          const errorKeys = Object.keys(errors);
                          if (errorKeys.length > 0) {
                            const newTouched = { ...touched };
                            errorKeys.forEach((key) => {
                              newTouched[key] = true;
                            });
                            setTouched(newTouched);
                          }
                        }}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </Button>
                    </Box>
                    {!isDirty && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: "block", textAlign: "right", mt: 1 }}
                      >
                        Edit a field to enable submit.
                      </Typography>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Container>
      <ToastContainer autoClose={2000} />
      <Dialog
        open={statutoryDuplicateDialog.open}
        onClose={closeStatutoryDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate statutory / bank detail</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {statutoryDuplicateDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatutoryDuplicateDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={ownerContactDuplicateDialog.open}
        onClose={closeOwnerContactDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Owner Contact Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This owner contact number is already registered for another vendor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeOwnerContactDuplicateDialog}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default VendorProfile;
